import { StartProjectBulkAdd } from 'api/graphQL/mutations/projectBulk.mutations'
import { GetProjectBulkAddStatus } from 'api/graphQL/queries/projectBulk.queries'
import {
    GetProjectBulkAddStatusResponse,
    ProjectBulkAddResult,
    ProjectBulkAddStatus,
    StartProjectBulkAddInput,
    StartProjectBulkAddResponse,
} from 'api/graphQL/types/projectBulk.types'
import PollableBackendProcessProgressDialog from 'components/ToMoveToSCS/PollableBackendProcessProgressDialog'
import React from 'react'

/** Properties for the {@link ProjectBulkAddProgressDialog} component. */
interface ProjectBulkAddProgressDialogProps {
    /** The number of elements the user selected. Note: This may be different from the count of elements in the input
     * becuase assembly elements get filtered out of the input. Showing this number in the dialog text avoids confusion
     * by making sure the count in the dialog text matches the selected-element count in the UI. */
    selectedElementCount: number

    /** Input parameter for the mutation that starts the process of adding elements to the project. */
    input: StartProjectBulkAddInput

    /** Called when the process is completed (with or without errors).
     * @param result - The result if there were no errors. Null if there was an error.
     */
    onCompleted: (result: Partial<ProjectBulkAddResult>) => void

    /** Called if the API reports an error during the process.
     * @param errorMessage - The error message returned by the API.
     */
    onError: (errorMessage: string | null | undefined) => void
}

/** Dialog that shows the progress of adding multiple elements to a project at once (aka Quick Add or Bulk Add). */
const ProjectBulkAddProgressDialog: React.FC<ProjectBulkAddProgressDialogProps> = ({
    selectedElementCount,
    input,
    onCompleted,
    onError,
}) => {
    const dialogText = React.useMemo(
        () =>
            `Adding ${selectedElementCount} item${
                selectedElementCount > 1 ? 's' : ''
            } to the project...`,
        [selectedElementCount]
    )
    return (
        <PollableBackendProcessProgressDialog<
            StartProjectBulkAddInput,
            StartProjectBulkAddResponse,
            'projectBulkAdd',
            GetProjectBulkAddStatusResponse,
            ProjectBulkAddStatus,
            'getProjectBulkAddStatus',
            ProjectBulkAddResult,
            'result'
        >
            dialogId="project-bulk-add-progress-dialog"
            dialogTitle="Add to Project Progress"
            dialogText={dialogText}
            onCompleted={onCompleted}
            onError={onError}
            startMutation={StartProjectBulkAdd}
            startMutationInput={input}
            startMutationResponseKey="projectBulkAdd"
            statusPropertyKey="getProjectBulkAddStatus"
            statusPropertyResultKey="result"
            statusQuery={GetProjectBulkAddStatus}
        />
    )
}

export default ProjectBulkAddProgressDialog
